import { useCallback, useEffect, useMemo, useState } from "react";
import {
  FiTrendingUp,
  FiAward,
  FiBriefcase,
  FiBarChart2,
  FiShield,
  FiCheckCircle,
  FiMessageCircle,
  FiRefreshCw,
} from "react-icons/fi";

import axiosInstance from "../config/axiosInstance";
import { extractList } from "../utils/api";
import { useCompany } from "../hooks/useCompany";
import {
  getCatalog,
  getMyBilling,
  cancelEntitlement,
  formatBRL,
  formatDate,
  getErrorMessage,
  getErrorCode,
} from "../services/billingApi";
import CheckoutModal from "../components/checkout/CheckoutModal";
import CompanyRequiredState from "../components/CompanyRequiredState";
import NoData from "../components/NoData";
import "./ServicosPage.css";

const GROUP_META = {
  VISIBILIDADE: { label: "Visibilidade", icon: FiTrendingUp, tone: "brand" },
  CREDIBILIDADE: { label: "Credibilidade", icon: FiShield, tone: "blue" },
  SERVICO: { label: "Serviços", icon: FiBriefcase, tone: "green" },
  CONSULTORIA: { label: "Consultoria", icon: FiAward, tone: "purple" },
  DADOS: { label: "Dados e inteligência", icon: FiBarChart2, tone: "amber" },
};

const GROUP_ORDER = ["VISIBILIDADE", "CREDIBILIDADE", "DADOS", "SERVICO", "CONSULTORIA"];

const WHATSAPP_COMERCIAL =
  process.env.REACT_APP_WHATSAPP_COMERCIAL || "5511999999999";

/** Faixa de preço legível: "R$ 49 a R$ 149/mês" ou "a partir de R$ 97". */
const priceLabel = (offer) => {
  const suffix = offer.billingType === "RECURRING" ? "/mês" : "";

  if (offer.isFree) return "Gratuito";

  if (offer.isCustomPriced) {
    return `${formatBRL(offer.priceMinCents)} a ${formatBRL(offer.priceMaxCents)}${suffix}`;
  }

  if (offer.hasPriceRange) {
    return `a partir de ${formatBRL(offer.defaultPriceCents)}${suffix}`;
  }

  return `${formatBRL(offer.defaultPriceCents)}${suffix}`;
};

const ServicosPage = () => {
  const { company } = useCompany();

  const [catalog, setCatalog] = useState(null);
  const [billing, setBilling] = useState(null);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [errorCode, setErrorCode] = useState(null);

  // Fluxo de contratação: oferta selecionada + alvo escolhido.
  const [pendingOffer, setPendingOffer] = useState(null);
  const [targetValue, setTargetValue] = useState("");
  const [checkoutOfferItem, setCheckoutOfferItem] = useState(null);
  const [checkoutMetadata, setCheckoutMetadata] = useState({});
  const [message, setMessage] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErrorCode(null);
    try {
      const [catalogData, billingData] = await Promise.all([getCatalog(), getMyBilling()]);
      setCatalog(catalogData);
      setBilling(billingData);
      setError(null);
    } catch (loadError) {
      setErrorCode(getErrorCode(loadError));
      setError(getErrorMessage(loadError, "Não foi possível carregar os serviços."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Produtos e categorias alimentam o seletor de alvo dos add-ons.
  useEffect(() => {
    if (!company?.id) return;

    axiosInstance
      .get("/products", { params: { itemsPerPage: -1, companyId: company.id } })
      .then((response) => setProducts(extractList(response)))
      .catch(() => setProducts([]));

    axiosInstance
      .get("/categories", { params: { itemsPerPage: -1 } })
      .then((response) => setCategories(extractList(response)))
      .catch(() => setCategories([]));
  }, [company?.id]);

  const offers = useMemo(() => catalog?.offers || [], [catalog]);

  const grouped = useMemo(() => {
    const map = new Map();
    for (const offer of offers) {
      if (!map.has(offer.group)) map.set(offer.group, []);
      map.get(offer.group).push(offer);
    }
    return GROUP_ORDER.filter((group) => map.has(group)).map((group) => ({
      group,
      offers: map.get(group),
    }));
  }, [offers]);

  const activeByCode = useMemo(() => {
    const map = new Map();
    for (const item of billing?.entitlements?.activeEntitlements || []) {
      if (!map.has(item.offerCode)) map.set(item.offerCode, []);
      map.get(item.offerCode).push(item);
    }
    return map;
  }, [billing]);

  const currentPlanCode = billing?.entitlements?.planCode;

  const handleContract = (offer) => {
    setMessage(null);

    // Ofertas que precisam de alvo abrem primeiro o seletor.
    if (offer.requiredFields?.length) {
      setPendingOffer(offer);
      setTargetValue("");
      return;
    }

    setCheckoutMetadata({});
    setCheckoutOfferItem(offer);
  };

  const confirmTarget = () => {
    if (!pendingOffer || !targetValue) return;

    const field = pendingOffer.requiredFields[0];
    setCheckoutMetadata({ [field]: targetValue });
    setCheckoutOfferItem(pendingOffer);
    setPendingOffer(null);
  };

  const handleCancelEntitlement = async (entitlementId) => {
    if (!window.confirm("Cancelar a renovação deste benefício?")) return;

    try {
      const result = await cancelEntitlement(entitlementId);
      setMessage(result?.message || "Renovação cancelada.");
      await load();
    } catch (cancelError) {
      setMessage(getErrorMessage(cancelError, "Não foi possível cancelar."));
    }
  };

  const openWhatsApp = (offer) => {
    const text = encodeURIComponent(
      `Olá! Tenho interesse em "${offer.name}" na Shopalli. Podem me enviar uma proposta?`
    );
    window.open(`https://wa.me/${WHATSAPP_COMERCIAL}?text=${text}`, "_blank", "noreferrer");
  };

  if (loading) {
    return (
      <div className="servicos fade-in">
        <div className="page-header">
          <div>
            <h2>Carregando serviços...</h2>
          </div>
        </div>
      </div>
    );
  }

  // Sem empresa cadastrada não é erro: é uma etapa que ainda falta.
  if (errorCode === "COMPANY_REQUIRED") {
    return (
      <div className="servicos fade-in">
        <CompanyRequiredState
          title="Falta cadastrar sua empresa"
          description="Os serviços avulsos são contratados pela empresa anunciante. Preencha os dados do seu CNPJ para liberar esta área."
        />
      </div>
    );
  }

  if (error) {
    return (
      <div className="servicos fade-in">
        <div className="page-header">
          <div>
            <h2>Não conseguimos carregar</h2>
            <p>{error}</p>
          </div>
        </div>
        <button type="button" className="button button--primary" onClick={load}>
          <FiRefreshCw /> Tentar novamente
        </button>
      </div>
    );
  }

  return (
    <div className="servicos fade-in page-stack">
      <div className="page-header">
        <div>
          <h2>Serviços e destaques</h2>
          <p>
            Amplie o alcance dos seus produtos com destaques, banners e serviços
            da equipe Shopalli. Contrate direto pelo painel, sem comissão sobre venda.
          </p>
        </div>
      </div>

      {message && <div className="servicos__message">{message}</div>}

      {/* ── Benefícios já ativos ───────────────────────────────────────── */}
      {activeByCode.size > 0 && (
        <section className="section-card">
          <h3 className="card-title">Contratados por você</h3>
          <div className="servicos__active-list">
            {[...activeByCode.entries()].flatMap(([code, items]) =>
              items.map((item) => {
                const offer = offers.find((o) => o.code === code);
                return (
                  <div key={item.id} className="servicos__active">
                    <span className="servicos__active-icon">
                      <FiCheckCircle />
                    </span>
                    <div>
                      <strong>{offer?.name || code}</strong>
                      <small>
                        {item.endsAt
                          ? `Válido até ${formatDate(item.endsAt)}`
                          : "Sem prazo de expiração"}
                        {item.autoRenew ? " · renovação automática" : ""}
                      </small>
                    </div>
                    {item.autoRenew && (
                      <button
                        type="button"
                        className="servicos__active-cancel"
                        onClick={() => handleCancelEntitlement(item.id)}
                      >
                        Cancelar renovação
                      </button>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </section>
      )}

      {/* ── Catálogo por grupo ─────────────────────────────────────────── */}
      {grouped.length === 0 ? (
        <NoData
          title="Nenhum serviço disponível"
          description="Assim que novos serviços forem publicados eles aparecem aqui."
        />
      ) : (
        grouped.map(({ group, offers: groupOffers }) => {
          const meta = GROUP_META[group] || { label: group, icon: FiBriefcase, tone: "brand" };
          const Icon = meta.icon;

          return (
            <section key={group} className="servicos__group">
              <header className="servicos__group-head">
                <span className="servicos__group-icon" data-tone={meta.tone}>
                  <Icon />
                </span>
                <h3>{meta.label}</h3>
              </header>

              <div className="servicos__grid">
                {groupOffers.map((offer) => {
                  const isActive = activeByCode.has(offer.code);
                  const blockedByPlan =
                    offer.requiresPlanCode && offer.requiresPlanCode !== currentPlanCode;

                  return (
                    <article
                      key={offer.code}
                      className={`servico-card${isActive ? " servico-card--active" : ""}`}
                    >
                      {isActive && <span className="servico-card__flag">Ativo</span>}

                      <h4>{offer.name}</h4>
                      <p className="servico-card__desc">{offer.description}</p>

                      <p className="servico-card__price">{priceLabel(offer)}</p>

                      {offer.strategicRole && (
                        <p className="servico-card__role">{offer.strategicRole}</p>
                      )}

                      {blockedByPlan ? (
                        <p className="servico-card__locked">
                          Disponível para assinantes do plano{" "}
                          {offer.requiresPlanCode.replace("_", " ").toLowerCase()}.
                        </p>
                      ) : offer.selfServiceEnabled ? (
                        <button
                          type="button"
                          className="button button--primary servico-card__cta"
                          onClick={() => handleContract(offer)}
                        >
                          {offer.isFree ? "Ativar grátis" : "Contratar"}
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="button button--ghost servico-card__cta"
                          onClick={() => openWhatsApp(offer)}
                        >
                          <FiMessageCircle /> Falar com o comercial
                        </button>
                      )}
                    </article>
                  );
                })}
              </div>
            </section>
          );
        })
      )}

      {/* ── Seletor de alvo (produto a destacar / categoria do banner) ─── */}
      {pendingOffer && (
        <div className="servicos__target-overlay" role="dialog" aria-modal="true">
          <div className="servicos__target">
            <h4>{pendingOffer.name}</h4>

            {pendingOffer.requiredFields[0] === "productId" ? (
              <>
                <p className="checkout__muted">Qual produto você quer destacar?</p>
                {products.length === 0 ? (
                  <p className="checkout__error">
                    Você ainda não tem produtos cadastrados.
                  </p>
                ) : (
                  <select value={targetValue} onChange={(e) => setTargetValue(e.target.value)}>
                    <option value="">Selecione um produto</option>
                    {products.map((product) => (
                      <option key={product.id} value={product.id}>
                        {product.title}
                      </option>
                    ))}
                  </select>
                )}
              </>
            ) : (
              <>
                <p className="checkout__muted">Em qual categoria o banner deve aparecer?</p>
                <select value={targetValue} onChange={(e) => setTargetValue(e.target.value)}>
                  <option value="">Selecione uma categoria</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.nome || category.name}
                    </option>
                  ))}
                </select>
              </>
            )}

            <div className="servicos__target-actions">
              <button
                type="button"
                className="button button--ghost"
                onClick={() => setPendingOffer(null)}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="button button--primary"
                disabled={!targetValue}
                onClick={confirmTarget}
              >
                Continuar
              </button>
            </div>
          </div>
        </div>
      )}

      <CheckoutModal
        isOpen={Boolean(checkoutOfferItem)}
        onClose={() => setCheckoutOfferItem(null)}
        item={checkoutOfferItem}
        kind="OFFER"
        metadata={checkoutMetadata}
        publicKey={catalog?.gateway?.publicKey}
        onSuccess={load}
      />
    </div>
  );
};

export default ServicosPage;
